import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../../redux/store';
import { GroupLite, GroupSummary } from '../types/Response';

type PageIndex = number;

export interface CursorPage<T> {
	items: T[];
}

export interface GroupState {
	page: number;
	rowsPerPage: number;

	// cursor paging
	cursors: Array<number | null>;
	pageCache: Record<PageIndex, CursorPage<GroupLite>>;
	nextCursorByPage: Record<PageIndex, number | null>;

	virtualCount: number; // for table UX
	summary?: GroupSummary | null;
}

const initialState: GroupState = {
	page: 0,
	rowsPerPage: 50,
	cursors: [null],
	pageCache: {},
	nextCursorByPage: {},
	virtualCount: 0,
	summary: null,
};

const slice = createSlice({
	name: 'groups',
	initialState,
	reducers: {
		resetGroupState: () => initialState,

		resetGroupCursorPaging(state) {
			state.page = 0;
			state.cursors = [null];
			state.pageCache = {};
			state.nextCursorByPage = {};
			state.virtualCount = 0;
		},

		setGroupRowsPerPage(state, action: PayloadAction<number>) {
			state.rowsPerPage = action.payload;
		},

		setGroupPage(state, action: PayloadAction<number>) {
			state.page = action.payload;
		},

		setGroupPageData(
			state,
			action: PayloadAction<{ page: number; items: GroupLite[]; nextCursor?: number | null }>,
		) {
			const { page, items, nextCursor } = action.payload;

			state.pageCache[page] = { items };
			state.nextCursorByPage[page] = nextCursor ?? null;

			state.cursors[page + 1] = nextCursor ?? null;

			const maxLoadedPage = Math.max(0, ...Object.keys(state.pageCache).map(n => +n));
			const hasNext = state.nextCursorByPage[maxLoadedPage] != null;
			state.virtualCount = (maxLoadedPage + 1) * state.rowsPerPage + (hasNext ? 1 : 0);
		},

		setGroupSummary(state, action: PayloadAction<GroupSummary | null | undefined>) {
			state.summary = action.payload ?? null;
		},
	},
});

export const {
	resetGroupState,
	resetGroupCursorPaging,
	setGroupRowsPerPage,
	setGroupPage,
	setGroupPageData,
	setGroupSummary,
} = slice.actions;

export default slice.reducer;

export const selectGroupPage = (s: RootState) => s.groups.page;
export const selectGroupRowsPerPage = (s: RootState) => s.groups.rowsPerPage;
export const selectGroupSummary = (s: RootState) => s.groups.summary;

export const selectGroupItems = (s: RootState) => {
	const p = s.groups.page;
	return s.groups.pageCache[p]?.items ?? [];
};

export const selectGroupCount = (s: RootState) => s.groups.virtualCount;
export const selectGroupCursorForPage = (p: number) => (s: RootState) =>
	s.groups.cursors[p] ?? null;
export const selectHasCachedGroupPage = (p: number) => (s: RootState) =>
	Boolean(s.groups.pageCache[p]);
